import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import FormContainer from '../CustomElements/FormContainer';
import Selector from '../CustomElements/Selector';
import Input from '../CustomElements/Input';
import CustomButton from '../CustomElements/CustomButton';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useOptionsLoader from '../../customHook/useOptionsLoader';
import useFormDataChanged from '../../customHook/useFormDataChanged';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { extractChangedFields } from '../../utils/extractChangedFields';
import { convertStringValuesToNumbers } from '../../utils/convertStringValuesToNumbers';
import { omitFields } from '../../utils/omitFields';
// Действия Redux
import { newSemaphoreFromServer, editSemaphoreFromServer } from '../../redux/actions/infocards.action';
import { load, remove } from '../../redux/actions/loader.action';
import { initOptionsRailChainsFromServer, initOptionsSectionFromServer } from '../../redux/actions/options.action';

// компонент для добавления и редактирования светофора
function AddOrEditSemaphore({
  closeModal,
  road,
  stationId,
  isEdit,
  currentSemaphore,
  addSuccess,
  setAddSuccess,
  isAdmin,
  t,
}) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // Получаем опции
  const { optionsSemaphoreDirection, stations, railChainsPerStation } = useSelector((state) => state.options);

  // Закрывает модальное окно после успешного добавления нового устройства
  const handlerCloseModal = () => {
    setAddSuccess(false);
    closeModal();
  };

  // Состояние для формы. Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () => ({
      id: currentSemaphore?.semaphoreId || '',
      name: currentSemaphore?.name || '',
      direction: currentSemaphore?.direction || '',
      stationId: currentSemaphore?.stationId || stationId || '',
      railChainBeforeId: currentSemaphore?.railChainBeforeId || '',
      railChainAfterId: currentSemaphore?.railChainAfterId || '',
      railChainBeforeStation: currentSemaphore?.railChainBeforeStation || stationId || '',
      railChainAfterStation: currentSemaphore?.railChainAfterStation || stationId || '',
    }),
    [currentSemaphore, stationId]
  );

  //состояние для формы добавления нового светофора
  const [formData, setFormData] = useState(initialFormData);

  // состояние для необязательных полей
  const [optionalFields] = useState([
    'railChainBeforeStation',
    'railChainBeforeId',
    'railChainAfterStation',
    'railChainAfterId',
  ]);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);
  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // Проверяем если не админ ничего не делаем
    if (!isAdmin) return;
    // Переводим в состояние загрузки
    dispatch(load());
    // массив полей, которые не нужно отправлять на сервер
    const excludedFields = ['stationId', 'railChainBeforeStation', 'railChainAfterStation'];
    const semaphoreId = Number(formData?.id);
    const stationId = Number(formData?.stationId);
    // Если не редактируем
    if (!isEdit) {
      // Убираем ненужные поля из формы
      const formWithoutExcluded = omitFields(formData, excludedFields);
      // Удаляем пустые строки
      const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcluded);
      // Преобразуем нужные строковые значения в числа
      const transformedPayload = convertStringValuesToNumbers(formWithoutEmptyStrings, ['id']);
      // Отправляем данные на сервер через Redux
      dispatch(newSemaphoreFromServer(transformedPayload, stationId));
    }
    // Если редактируем
    if (isEdit) {
      // Оставляем только измененные поля
      const changedFields = extractChangedFields(currentSemaphore, formData);
      // Убираем ненужные поля из формы
      const formWithoutExcluded = omitFields(changedFields, ['id', ...excludedFields]);
      // Отправляем данные на сервер через Redux
      dispatch(editSemaphoreFromServer({ payload: formWithoutExcluded, semaphoreId, stationId }));
    }
  };

  //хук для запроса опций  RC и контроля селекта
  const messageForSelectRC = t?.selectStation || 'Выберите станцию';
  const rcBeforeControl = useOptionsLoader(
    formData,
    setFormData,
    ['railChainBeforeId'],
    'railChainBeforeStation',
    initOptionsRailChainsFromServer,
    messageForSelectRC
  );

  const rcAfterControl = useOptionsLoader(
    formData,
    setFormData,
    ['railChainAfterId'],
    'railChainAfterStation',
    initOptionsRailChainsFromServer,
    messageForSelectRC
  );

  useEffect(() => {
    dispatch(initOptionsSectionFromServer(road));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Функция для получения опций для Selector
  const getSelectorOptions = (stationKey) => railChainsPerStation?.[formData?.[stationKey]] || [];

  // Функция для получения значения для Selector
  const getSelectorValue = (stationKey, chainKey) => {
    const options = getSelectorOptions(stationKey);
    return formData?.[chainKey] ? options?.find((option) => option?.value === formData?.[chainKey]) : '';
  };
  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      setAddSuccess(true);
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  return (
    <CustomContainer>
      {/* <div className="add-device"> */}
      <CustomTitle
        titleText={
          isEdit ? t?.editSemaphore || 'РЕДАКТИРОВАНИЕ СВЕТОФОРА' : t?.newSemaphore || 'ДОБАВЛЕНИЕ НОВОГО СВЕТОФОРА'
        }
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={
            isEdit ? t?.semaphoreEdited || 'Изменения сохранены' : t?.semaphoreAdded || 'Новый светофор добавлен!'
          }
          onConfirm={handlerCloseModal}
          addNew={t?.goToList || 'К списку'}
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <Input
            column="left"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="id"
            label={`${t?.semaphoreId} *` || 'ID светофора *'}
            placeholder={t?.enterSemaphoreId || 'Введите ID светофора'}
            width="100%"
            height="var(--sadr-font-size-60)"
            format="######"
            maxLength={9}
            isCoordinate={true}
            isAllowed={undefined}
            value={formData?.id}
            isDisabled={isEdit}
          />

          <Selector
            column="left"
            options={stations || []}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'railChainBeforeStation', selectedOption, 'select')
            }
            label={t?.stationBefore || 'Станция РЦ перед светофором'}
            placeholder={t?.selectStation || 'Выберите станцию'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="railChainBeforeStation"
            value={
              formData?.railChainBeforeStation
                ? stations?.find((option) => option?.value === formData?.railChainBeforeStation)
                : ''
            }
          />

          <Selector
            column="left"
            options={getSelectorOptions('railChainBeforeStation')}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'railChainBeforeId', selectedOption, 'select')
            }
            label={t?.railBefore || 'РЦ перед светофором'}
            placeholder={t?.selectRailChain || 'Выберите РЦ'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="railChainBeforeId"
            isDisabled={rcBeforeControl?.isSelectorDisabled}
            message={rcBeforeControl?.message}
            customClick={rcBeforeControl?.onSelectorChange}
            value={getSelectorValue('railChainBeforeStation', 'railChainBeforeId')}
          />

          <Selector
            column="left"
            options={optionsSemaphoreDirection || []}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'direction', selectedOption, 'select')
            }
            placeholder={t?.selectDirection || 'Выберите направление'}
            label={t?.semaphoreDirection || 'Направление движения по светофору *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="direction"
            value={
              formData?.direction
                ? optionsSemaphoreDirection?.find((option) => option.value === formData?.direction)
                : ''
            }
          />
          <Input
            column="right"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="name"
            label={t?.semaphoreName || 'Название светофора *'}
            placeholder={t?.enterSemaphoreName || 'Введите название светофора'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={formData?.name}
          />

          <Selector
            column="right"
            options={stations || []}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'railChainAfterStation', selectedOption, 'select')
            }
            label={t?.stationAfter || 'Станция РЦ за светофором'}
            placeholder={t?.selectStation || 'Выберите станцию'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="railChainAfterStation"
            value={
              formData?.railChainAfterStation
                ? stations?.find((option) => option?.value === formData?.railChainAfterStation)
                : ''
            }
          />

          <Selector
            column="right"
            options={getSelectorOptions('railChainAfterStation')}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'railChainAfterId', selectedOption, 'select')
            }
            label={t?.railAfter || 'РЦ за светофором'}
            placeholder={t?.selectRailChain || 'Выберите РЦ'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="railChainAfterId"
            isDisabled={rcAfterControl?.isSelectorDisabled}
            message={rcAfterControl?.message}
            customClick={rcAfterControl?.onSelectorChange}
            value={getSelectorValue('railChainAfterStation', 'railChainAfterId')}
          />

          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            margin={'0 0 var(--sadr-font-size-20) 0'}
            text={isEdit ? t?.saveChanges || 'Сохранить изменения' : t?.addSemaphore || 'Добавить светофор'}
            onClick={handlerSubmit}
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default AddOrEditSemaphore;
